import { useEffect, useState } from 'react';
import { deskApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { useEmployeeOptions } from '@/hooks/useEmployeeOptions';
import type { DeskReportsBundle } from '@/types/desk';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function DeskReportsPage() {
  const { getLabel: getEmployeeLabel } = useEmployeeOptions();
  const [reports, setReports] = useState<DeskReportsBundle | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);
  const agentPerformance = reports?.agentPerformance ?? [];
  const { rows: paginatedAgentRows, tablePaginationProps: agentPaginationProps } =
    useClientPaginatedRows(agentPerformance);

  useEffect(() => {
    deskApi.reports()
      .then(setReports)
      .catch((err) => showApiError(err, 'Failed to load desk reports'));
  }, []);

  const download = async (format: 'CSV' | 'PDF') => {
    setExporting(format);
    try {
      const response = await deskApi.exportReport(format);
      const contentType = response.headers['content-type'];
      const blob = new Blob([response.data], {
        type: typeof contentType === 'string' ? contentType : 'application/octet-stream',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `desk_reports.${format.toLowerCase()}`;
      a.click();
      URL.revokeObjectURL(url);
      notify.success(`Report exported as ${format}`);
    } catch (err) {
      showApiError(err, 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Desk reports"
        description="Ticket volume, SLA breaches, and agent performance"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Desk', to: '/desk' },
          { label: 'Reports' },
        ]}
        actions={
          <div className="flex gap-2">
            {(['CSV', 'PDF'] as const).map((fmt) => (
              <Button key={fmt} variant="secondary" loading={exporting === fmt} onClick={() => void download(fmt)}>
                Export {fmt}
              </Button>
            ))}
          </div>
        }
      />

      {!reports ? (
        <p className="text-slate-500 p-6">Loading reports…</p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
            <StatCard label="Total tickets" value={reports.totalTickets} />
            <StatCard label="Open tickets" value={reports.openTickets} />
            <StatCard label="Unassigned" value={reports.unassignedTickets} />
            <StatCard label="SLA breaches" value={reports.slaBreachedTickets} />
            {reports.averageCsat != null && (
              <StatCard label="Average CSAT" value={reports.averageCsat.toFixed(1)} />
            )}
          </div>

          {agentPerformance.length > 0 && (
            <div className="card overflow-hidden">
              <h3 className="font-semibold p-4 border-b">Agent performance</h3>
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3">Employee</th>
                    <th className="text-right p-3">Assigned</th>
                    <th className="text-right p-3">Resolved</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAgentRows.map((row) => (
                    <tr key={row.employeeId} className="border-t">
                      <td className="p-3">{getEmployeeLabel(row.employeeId, true)}</td>
                      <td className="p-3 text-right">{row.assignedTickets}</td>
                      <td className="p-3 text-right">{row.resolvedTickets}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePagination {...agentPaginationProps} hasRows={agentPerformance.length > 0} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="panel">
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-semibold text-slate-800 mt-1">{value}</p>
    </div>
  );
}
